import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';

export type LeadStatusSummary = { _id: string; code: string; name: string };

export type LeadSubStatusSummary = { _id: string; name: string };

export type LeadSubStatusOption = {
  _id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
};

export type LeadArchiveFilter = 'active' | 'archived' | 'all';

export type LeadOwnerSummary = {
  userId: string;
  displayName: string;
};

export type LeadDto = {
  _id: string;
  franchiseId?: string;
  statusId: string;
  status: LeadStatusSummary;
  subStatusId?: string;
  subStatus?: LeadSubStatusSummary;
  fullName: string;
  email: string;
  phoneNumber: string;
  preferredDestination?: string;
  message?: string;
  latestNote?: string;
  sourcePage?: string;
  leadSource?: string;
  whatsappNumber?: string;
  city?: string;
  state?: string;
  country?: string;
  qualification?: string;
  percentageOrCgpa?: string;
  passingYear?: string;
  institution?: string;
  preferredCourse?: string;
  intake?: string;
  budgetRange?: string;
  passportStatus?: string;
  englishTestStatus?: string;
  loanRequired?: string;
  stream?: string;
  section?: string;
  campaignName?: string;
  adSetName?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  financeTotalAmount?: number;
  financePaidAmount?: number;
  financePendingAmount?: number;
  profilePhotoMediaAssetId?: string;
  profilePhotoUrl?: string;
  isArchived: boolean;
  archivedAt?: string;
  archivedByUserId?: string;
  updatedByUserId?: string;
  ownerUserId?: string;
  assignedAt?: string;
  assignedByUserId?: string;
  owner?: LeadOwnerSummary;
  convertedApplicantId?: string;
  isMerged?: boolean;
  mergedIntoLeadId?: string;
  mergedAt?: string;
  mergedByUserId?: string;
  mergedFromLeadIds?: string[];
  mergeId?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type LeadExportRow = {
  leadId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  whatsappNumber: string;
  preferredDestination: string;
  stream: string;
  section: string;
  leadSource: string;
  status: string;
  assignedTo: string;
  city: string;
  state: string;
  country: string;
  qualification: string;
  percentageOrCgpa: string;
  passingYear: string;
  institution: string;
  preferredCourse: string;
  intake: string;
  budgetRange: string;
  passportStatus: string;
  englishTestStatus: string;
  loanRequired: string;
  message: string;
  createdAt: string;
};

export type PaginatedLeads = {
  items: LeadDto[];
  total: number;
  page: number;
  pageSize: number;
};

export type LeadStatusOption = { _id: string; code: string; name: string };

export type LeadTransitionOption = {
  _id: string;
  transitionName: string;
  description?: string;
  requiresComment: boolean;
  toStatus: LeadStatusSummary;
};

export type WorkflowDocumentationStatus = {
  _id: string;
  code: string;
  name: string;
  displayOrder: number;
};

export type WorkflowDocumentationStep = {
  _id: string;
  stepNumber: number;
  transitionName: string;
  description?: string;
  requiresComment: boolean;
  triggerType: 'MANUAL' | 'AUTOMATIC';
  actionButtonName: string;
  fromStatus: WorkflowDocumentationStatus;
  toStatus: WorkflowDocumentationStatus;
};

export type WorkflowDocumentation = {
  statuses: WorkflowDocumentationStatus[];
  steps: WorkflowDocumentationStep[];
};

export type ListLeadsParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  statusId?: string;
  preferredDestination?: string;
  stream?: string;
  section?: string;
  createdFrom?: string;
  createdTo?: string;
  archiveFilter?: LeadArchiveFilter;
  ownerUserId?: string;
  showEnrolled?: boolean;
  showDisqualified?: boolean;
  createdToday?: boolean;
  includeEnrolled?: boolean;
  pendingTasksAssignedToMe?: boolean;
};

export type UpdateLeadPayload = {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  city?: string;
  state?: string;
  country?: string;
  qualification?: string;
  percentageOrCgpa?: string;
  passingYear?: string;
  institution?: string;
  preferredDestination?: string;
  preferredCourse?: string;
  intake?: string;
  budgetRange?: string;
  passportStatus?: string;
  englishTestStatus?: string;
  loanRequired?: string;
  stream?: string;
  section?: string;
  leadSource?: string;
  message?: string;
  financeTotalAmount?: number | null;
  financePaidAmount?: number | null;
  financePendingAmount?: number | null;
  profilePhotoMediaAssetId?: string;
};

export type TransitionLeadPayload = {
  targetStatusId: string;
  comment?: string;
};

export type CreateLeadPayload = {
  fullName: string;
  email?: string;
  phoneNumber: string;
  preferredDestination: string;
  leadSource: string;
  message?: string;
  sourcePage?: string;
  whatsappNumber?: string;
  city?: string;
  state?: string;
  country?: string;
  qualification?: string;
  percentageOrCgpa?: string;
  passingYear?: string;
  institution?: string;
  preferredCourse?: string;
  intake?: string;
  budgetRange?: string;
  passportStatus?: string;
  englishTestStatus?: string;
  loanRequired?: string;
  stream?: string;
  section?: string;
  campaignName?: string;
  adSetName?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  allowDuplicate?: boolean;
};

function toQuery(params: ListLeadsParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.statusId) q.set('statusId', params.statusId);
  if (params.preferredDestination?.trim()) q.set('preferredDestination', params.preferredDestination.trim());
  if (params.stream?.trim()) q.set('stream', params.stream.trim());
  if (params.section?.trim()) q.set('section', params.section.trim());
  if (params.createdFrom) q.set('createdFrom', params.createdFrom);
  if (params.createdTo) q.set('createdTo', params.createdTo);
  if (params.archiveFilter) q.set('archiveFilter', params.archiveFilter);
  if (params.ownerUserId) q.set('ownerUserId', params.ownerUserId);
  if (params.showEnrolled) q.set('showEnrolled', 'true');
  if (params.showDisqualified) q.set('showDisqualified', 'true');
  if (params.createdToday) q.set('createdToday', 'true');
  if (params.includeEnrolled) q.set('includeEnrolled', 'true');
  if (params.pendingTasksAssignedToMe) q.set('pendingTasksAssignedToMe', 'true');
  const s = q.toString();
  return s ? `?${s}` : '';
}

@Injectable({ providedIn: 'root' })
export class LeadsService {
  private readonly http = inject(HttpClient);

  constructor(private readonly api: ApiService) {}

  listPlatform(params: ListLeadsParams = {}) {
    return this.api
      .get<ApiEnvelope<PaginatedLeads>>(`platform/leads${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  listStatuses() {
    return this.api
      .get<ApiEnvelope<LeadStatusOption[]>>('platform/leads/statuses')
      .pipe(map((r) => r.data));
  }

  getById(id: string) {
    return this.api.get<ApiEnvelope<LeadDto>>(`platform/leads/${id}`).pipe(map((r) => r.data));
  }

  update(id: string, payload: UpdateLeadPayload) {
    return this.api.patch<ApiEnvelope<LeadDto>>(`platform/leads/${id}`, payload).pipe(map((r) => r.data));
  }

  uploadProfilePhoto(id: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http
      .post<ApiEnvelope<LeadDto>>(`${this.api.baseUrl}/platform/leads/${id}/profile-photo`, form, {
        withCredentials: true,
      })
      .pipe(map((r) => r.data));
  }

  archive(id: string) {
    return this.api.post<ApiEnvelope<LeadDto>>(`platform/leads/${id}/archive`, {}).pipe(map((r) => r.data));
  }

  unarchive(id: string) {
    return this.api.post<ApiEnvelope<LeadDto>>(`platform/leads/${id}/unarchive`, {}).pipe(map((r) => r.data));
  }

  getTransitions(id: string) {
    return this.api
      .get<ApiEnvelope<LeadTransitionOption[]>>(`platform/leads/${id}/transitions`)
      .pipe(map((r) => r.data));
  }

  getWorkflowDocumentation() {
    return this.api
      .get<ApiEnvelope<WorkflowDocumentation>>('platform/leads/workflow')
      .pipe(map((r) => r.data));
  }

  transition(id: string, payload: TransitionLeadPayload) {
    return this.api
      .post<ApiEnvelope<LeadDto>>(`platform/leads/${id}/transition`, payload)
      .pipe(map((r) => r.data));
  }

  listSubStatuses() {
    return this.api
      .get<ApiEnvelope<LeadSubStatusOption[]>>(`platform/leads/sub-statuses`)
      .pipe(map((r) => r.data));
  }

  updateSubStatus(id: string, subStatusId: string | null) {
    return this.api
      .patch<ApiEnvelope<LeadDto>>(`platform/leads/${id}/sub-status`, { subStatusId })
      .pipe(map((r) => r.data));
  }

  create(payload: CreateLeadPayload) {
    return this.api.post<ApiEnvelope<LeadDto>>('platform/leads', payload).pipe(map((r) => r.data));
  }

  assignOwner(id: string, ownerUserId: string) {
    return this.api
      .post<ApiEnvelope<LeadDto>>(`platform/leads/${id}/assign`, { ownerUserId })
      .pipe(map((r) => r.data));
  }

  submitPublic(payload: CreateLeadPayload) {
    return this.api.post<ApiEnvelope<LeadDto>>('public/leads', payload).pipe(map((r) => r.data));
  }

  exportPlatform(params: ListLeadsParams = {}) {
    return this.api
      .get<ApiEnvelope<LeadExportRow[]>>(`platform/leads/export${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  getImportFieldDefinitions() {
    return this.api
      .get<ApiEnvelope<import('../models/lead-import.model').LeadImportFieldDefinition[]>>(
        'platform/leads/import/field-definitions',
      )
      .pipe(map((r) => r.data));
  }

  previewImport(payload: {
    columnMapping: Record<string, string>;
    rows: { data: Record<string, string> }[];
    streamValueMappings?: Record<string, string>;
  }) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-import.model').LeadImportPreviewResult>>(
        'platform/leads/import/preview',
        payload,
      )
      .pipe(map((r) => r.data));
  }

  executeImport(payload: {
    columnMapping: Record<string, string>;
    rows: { data: Record<string, string> }[];
    streamValueMappings?: Record<string, string>;
    duplicateAction: import('../models/lead-import.model').LeadImportDuplicateAction;
    includeWarnings: boolean;
  }) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-import.model').LeadImportExecuteResult>>(
        'platform/leads/import/execute',
        payload,
      )
      .pipe(map((r) => r.data));
  }

  previewDuplicates() {
    return this.api
      .get<ApiEnvelope<import('../models/lead-duplicates.model').LeadDuplicatesPreview>>(
        'platform/leads/duplicates/preview',
      )
      .pipe(map((r) => r.data));
  }

  removeDuplicates(leadIds: string[]) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-duplicates.model').LeadDuplicatesRemoveResult>>(
        'platform/leads/duplicates/remove',
        { leadIds },
      )
      .pipe(map((r) => r.data));
  }

  previewMerge(leadIds: string[]) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-merge.model').LeadMergePreview>>(
        'platform/leads/merge/preview',
        { leadIds },
      )
      .pipe(map((r) => r.data));
  }

  executeMerge(payload: {
    masterLeadId: string;
    sourceLeadIds: string[];
    fieldSelections: Record<string, string>;
    expectedUpdatedAt?: Record<string, string>;
    clientMergeId?: string;
  }) {
    return this.api
      .post<ApiEnvelope<import('../models/lead-merge.model').LeadMergeExecuteResult>>(
        'platform/leads/merge/execute',
        payload,
      )
      .pipe(map((r) => r.data));
  }
}
